import React, { useCallback, useMemo, useState } from "react";

import styles from "./NotificacionesCotizacion.module.scss";

const STORAGE_KEY = "notif_cotizaciones_seen";
const DAYS_LOOKBACK = 30;

const getSeen = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
  } catch {
    return {};
  }
};

const saveSeen = (map) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
};

const EXTERNAL_ROLES = new Set(["agencia", "cliente", "edificio", "inversor"]);

const NotificacionesCotizacion = ({ cotizaciones = [], loading = false, rol = "" }) => {
  const canMark = !!rol && !EXTERNAL_ROLES.has(rol);
  const [seen, setSeen] = useState(getSeen);
  const [isOpen, setIsOpen] = useState(true);

  // Filtrar últimos 30 días, solo borradores
  const recientes = useMemo(() => {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - DAYS_LOOKBACK);
    return cotizaciones
      .filter((c) => {
        if (!c.created_at) return false;
        if (c.estado === "ejecutada") return false;
        return new Date(c.created_at) >= cutoff;
      })
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [cotizaciones]);

  const isSeen = useCallback(
    (cot) => {
      const seenAt = seen[cot.id];
      if (!seenAt) return false;
      return new Date(seenAt) >= new Date(cot.created_at);
    },
    [seen]
  );

  const unseenCount = useMemo(() => recientes.filter((c) => !isSeen(c)).length, [recientes, isSeen]);

  const handleToggleSeen = useCallback(
    (cot) => {
      const alreadySeen = isSeen(cot);
      let updated;
      if (alreadySeen) {
        updated = { ...seen };
        delete updated[cot.id];
      } else {
        updated = { ...seen, [cot.id]: new Date().toISOString() };
      }
      setSeen(updated);
      saveSeen(updated);
    },
    [seen, isSeen]
  );

  if (loading) return null;
  if (recientes.length === 0) return null;

  const formatFecha = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    return d.toLocaleDateString("es-CL", { day: "2-digit", month: "short" });
  };

  const formatMonto = (amount) => {
    const num = Number(amount) || 0;
    if (num === 0) return "-";
    return num.toLocaleString("es-CL", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.header} onClick={() => setIsOpen(!isOpen)}>
        <span className={styles.headerTitle}>Nuevas Cotizaciones</span>
        {unseenCount > 0 && <span className={styles.badge}>{unseenCount}</span>}
        <span className={`${styles.chevron} ${isOpen ? styles.open : ""}`}>&#9660;</span>
      </div>

      {isOpen && (
        <div className={styles.content}>
          {recientes.map((cot) => {
            const cotSeen = isSeen(cot);
            const montoDisplay = cot.moneda_dolar
              ? `USD ${formatMonto(cot.final_amount || cot.inversion_final_total)}`
              : formatMonto(cot.final_amount || cot.inversion_final_total);

            return (
              <div
                key={cot.id}
                className={[styles.card, cotSeen ? styles.seen : "", canMark ? styles.clickable : ""].join(" ")}
                onClick={canMark ? () => handleToggleSeen(cot) : undefined}
                title={canMark ? (cotSeen ? "Click para desmarcar" : "Click para marcar como vista") : undefined}
              >
                {cotSeen && <span className={styles.seenCheck}>&#10003;</span>}
                <div className={styles.cardHeader}>
                  <span className={styles.codigo}>{cot.codigo_generado}</span>
                  <span className={styles.rolBadge}>{cot._rol_creador === "agencia" ? "Agencia" : "Cliente"}</span>
                </div>
                <div className={styles.cardBody}>
                  <div className={styles.cardRow}>
                    <span className={styles.cardLabel}>Fecha</span>
                    <span className={styles.cardValue}>{formatFecha(cot.created_at)}</span>
                  </div>
                  {cot._agencia_nombre && cot._agencia_nombre !== "-" && (
                    <div className={styles.cardRow}>
                      <span className={styles.cardLabel}>Agencia</span>
                      <span className={styles.cardValue}>{cot._agencia_nombre}</span>
                    </div>
                  )}
                  {cot._ejecutivo_nombre && cot._ejecutivo_nombre !== "-" && (
                    <div className={styles.cardRow}>
                      <span className={styles.cardLabel}>Ejecutivo</span>
                      <span className={styles.cardValue}>{cot._ejecutivo_nombre}</span>
                    </div>
                  )}
                  {cot.nombre_cotizacion && (
                    <div className={styles.cardRow}>
                      <span className={styles.cardLabel}>Descripción</span>
                      <span className={styles.cardValue}>{cot.nombre_cotizacion}</span>
                    </div>
                  )}
                  <div className={styles.cardRow}>
                    <span className={styles.cardLabel}>Inversión</span>
                    <span className={styles.cardValue}>{montoDisplay}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default NotificacionesCotizacion;
